import BaseService from "./BaseService";
import ProductRebilling, { IProductRebilling, IProductResponse } from "../models/api/ProductRebilling";
import keys from "../config/services";

const v2TestComapnyIds = [
  'Ydk7Yx1VsHhacT39iE4Z', // prod
  '5DP4iH6HLkQsiKESj6rh' // staging
]

const v2TestsLocationIds = [
  'Y8yvO6oRo1nWxyRrfxbr', // prod
  'eBG6WapS3v4ZqAMTxtYJ'
]

class ProductRebillingServiceV2 extends BaseService {
  constructor(baseUrl: string) {
    super(baseUrl);
  }

  public requests = {
    get: async (url: string, options = {}) => {
      return this.axiosInstance({
        url: this.baseUrl + url,
        method: 'GET',
        ...options,
      }).then(this.responseBody)
    },
    post: async (url: string, body: { [key: string]: any }, options = {}) =>
      this.axiosInstance({
        url: this.baseUrl + url,
        method: 'POST',
        data: body,
        ...options,
      }).then(this.responseBody),
    put: async (url: string, body: { [key: string]: any }) =>
      this.axiosInstance({
        url: this.baseUrl + url,
        method: 'PUT',
        data: body,
      }).then(this.responseBody),
    delete: async (url: string, options = {}) =>
      this.axiosInstance({
        url: this.baseUrl + url,
        method: 'DELETE',
        ...options,
      }).then(this.responseBody),
  }
}

class ProductRebillingService extends BaseService {
  private v2Service: ProductRebillingServiceV2
  constructor(baseUrl: string) {
    super(baseUrl);
  }

  private checkIfV2Enabled(entityId) {
    const isV2Enabled = v2TestComapnyIds.find(function(value) {
      return value == entityId
    }) || v2TestsLocationIds.find(function(value) {
      return value == entityId
    })

    return isV2Enabled
  }

  get = async (entityId: string, entityType: string, companyId?: string): Promise<IProductRebilling[]> => {
    try {
      let url = `/billing-config/${entityType}/${entityId}`;
      if(entityType === 'LOCATION'){
        url = url + `?companyId=${companyId}`
      }

      let response
      if (this.checkIfV2Enabled(entityId)) {
        this.v2Service = new ProductRebillingServiceV2(keys.SaaSBillingV2)
        response = await this.v2Service.requests.get(url);
      } else {
        response = await this.requests.get(url);
      }

      return response.data.map((row) => new ProductRebilling(row as IProductResponse));
    } catch (err) {
      throw err
    }
  }

  update = async (entityType: string, entityId: string, productRebilling: IProductRebilling, companyId?: string) => {
    try {
      let url = `/billing-config/${entityId}`;
      if(entityType === 'LOCATION'){
        url = url + `?companyId=${companyId}`
      }
      const updateData = {
        entityType: entityType,
        product: productRebilling.type,
        config: {
          markup: productRebilling.markup,
          enabled: productRebilling.rebillingEnabled,
          basePrice: productRebilling.price,
          optIn: productRebilling.productOptIn
        }
      }

      if (this.checkIfV2Enabled(entityId)) {
        this.v2Service = new ProductRebillingServiceV2(keys.SaaSBillingV2)
        await this.v2Service.requests.put(url,updateData);
      } else {
        await this.requests.put(url,updateData);
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Request failed");
      throw err
    }
  }

  getRebilling = async (companyId: string): Promise<IProductRebilling[]> => {
    try {
      let url = `/rebilling-config/${companyId}`;
      const { data }  = await this.requests.get(url);
      const rebillingData = data.map((row) => new ProductRebilling(row as IProductResponse));
      return rebillingData;
    } catch (err) {
      throw err
    }
  }

  updateRebilling = async (companyId: string, productRebilling: IProductRebilling) => {
    try {
      const updateData = {
        product: productRebilling.type,
        config: {
          markup: productRebilling.markup,
          enabled: productRebilling.rebillingEnabled,
          basePrice: productRebilling.price,
          optIn: productRebilling.productOptIn
        }
      }
      await this.requests.put(`/rebilling-config/${companyId}`,updateData);
    } catch (err) {
      alert(err?.response?.data?.message || "Request failed");
      throw err
    }
  }

  getByProduct = async (entityId: string, entityType: string, product: string): Promise<IProductRebilling> => {
    try {
      let url = `/billing-config/${entityType}/${entityId}/${product}`;

      let rebillingData
      if (this.checkIfV2Enabled(entityId)) {
        this.v2Service = new ProductRebillingServiceV2(keys.SaaSBillingV2)
        const {data}  = await this.v2Service.requests.get(url);

        rebillingData = new ProductRebilling(data[0] as IProductResponse);
      } else {
        const {data}  = await this.requests.get(url);

        rebillingData = new ProductRebilling(data[0] as IProductResponse);
      }

      return rebillingData;
    } catch (err) {
      throw err
    }
  }

  getByProductAndLocation =
    async (
      locationId: string,
      companyId: string,
      product: string): Promise<IProductResponse | undefined> => {
    try {
      let url = `/billing-config/LOCATION/${locationId}/${product}?companyId=${companyId}&optIn=true`;

      let productConfig
      if (this.checkIfV2Enabled(locationId)) {
        this.v2Service = new ProductRebillingServiceV2(keys.SaaSBillingV2)
        const {data} = await this.v2Service.requests.get(url);
        productConfig = data[0]
      } else {
        const {data} = await this.requests.get(url);
        productConfig = data[0]
      }

      if (productConfig)
        return productConfig as IProductResponse;
    } catch (err) {
      throw err
    }
  }
}

const instance = new ProductRebillingService(BaseService.servicesConfig.SaaSBilling)

export default instance
